/**
 * bimAvatars — a PERSON in the 3D view (card t_a41a75f6).
 *
 * The owner, 2026-10-10: *"can we make user avatar's 3d objects.. eg import glb
 * like for our dog. if no glb just icon.. if absent not shown on 3d. if present
 * then show at preset location.. this location could then be edited by each user
 * .. we would enter users 'sim' gravity mode. where we would control the
 * movement of the user via keys.. if user enters a building under the roof we
 * would cut the model..."*.
 *
 * So a person's 3D presence is THREE states, in the owner's own order:
 *
 *   · a GLB the person picked (or an admin picked for them) -> that model;
 *   · no GLB, but the person has a hub avatar image        -> that picture, as
 *     a billboard;
 *   · neither                                              -> not in the 3D
 *     view at all. Never a placeholder body.
 *
 * WHERE IT LIVES. The spawn position is per person AND per dashboard ("show at
 * preset location.. edited by each user"), and the write must be possible for
 * the person it belongs to — a resident cannot edit the owner's dashboard row,
 * but they may always edit their own user row. So the whole slot is the person's
 * own `users.preferences.bimAvatar` blob, which the hub already serves to every
 * signed-in client (`GET /api/people` carries `preferences`) and already accepts
 * back (`PUT /api/users/:userId {preferences}` — self or admin). No new hub
 * route, no dashboard-write rights needed:
 *
 *   preferences.bimAvatar = {
 *     v: 1,
 *     model: { url, name, bytes, uploadedAt, scale } | null,
 *     spawns: { <dashboardId>: { x, y, z, yawDeg, at } }
 *   }
 *
 * A person with no `bimAvatar` block at all is simply not configured — they
 * still appear when they have a hub avatar image, because that is the owner's
 * own fallback rule. Everything here is pure (no Vue, no three.js, no stores)
 * so the stored shape, its tolerance and the spawn rules are unit-testable
 * (`bimAvatars.test.ts`); the renderer is `useBimAvatars.ts`.
 */

export type Vec3 = [number, number, number];

/** The key of the slot inside `users.preferences`. */
export const BIM_AVATAR_PREFS_KEY = 'bimAvatar';

/** The schema tag of the slot — additive changes keep it. */
export const BIM_AVATAR_SCHEMA = 'torik-bim-avatar/v1';

/**
 * How tall a person's GLB is drawn, in metres (a person is the unit — the
 * model's own units are normalised away, exactly as `useOttoDog` normalises the
 * dog to 1.2 units for its FAB). The per-person `scale` multiplies this, so a
 * dog at `scale: 0.45` reads as a dog next to a person.
 *
 * HALVED to 0.85 on 2026-10-11 (card t_565b83bf): the owner, verbatim — *"the
 * user avatars are much too big. reduce to 50%.. also the dog is much too
 * big... let's try also 50%.."*. Both halves of the layer move together
 * (`BIM_AVATAR_BILLBOARD_HEIGHT_M` below), and the per-slot `model.scale`
 * multiplier keeps working on top of the new base exactly as it did on the old
 * one — so a slot's own numbers mean the same thing, at half the size.
 */
export const BIM_AVATAR_HEIGHT_M = 0.85;

/** The bounds a person's size multiplier is clamped into. */
export const BIM_AVATAR_MIN_SCALE = 0.15;
export const BIM_AVATAR_MAX_SCALE = 3;

/** The billboard's own height when the person's picture is used (metres) — halved with the models (card t_565b83bf). */
export const BIM_AVATAR_BILLBOARD_HEIGHT_M = 0.9;

/** How far outside the model's own bounds the default spawn sits (metres). */
export const BIM_AVATAR_SPAWN_MARGIN_M = 2.5;

/** Model slots a clipped/garbage URL is refused: a hub path or an http(s) URL. */
const URL_MAX_LENGTH = 2048;

export interface BimAvatarModel {
  /** The path the bytes are fetched from — hub-relative (`/models/…`) or absolute. */
  url: string;
  name: string;
  bytes: number;
  uploadedAt: string;
  /** Size multiplier over `BIM_AVATAR_HEIGHT_M` (1 = person-sized). */
  scale: number;
}

export interface BimAvatarSpawn {
  x: number;
  y: number;
  z: number;
  /** Facing about the world up axis (degrees) — the pose the avatar idles in. */
  yawDeg: number;
  /** When the spawn was last written (ISO) — for the editor's own line. */
  at?: string;
}

export interface BimAvatarSlot {
  /** The GLB this person brought, or null (then their picture, if any). */
  model: BimAvatarModel | null;
  /**
   * Spawn per dashboard id — the person's HOME spot is the dashboard's own row
   * (owner, 2026-10-18: *"Home position should be per dashboard settings"*). A
   * dashboard with no row for them stands them on its preset spot; there is no
   * person-wide fallback any more, because a spot belongs to a house view.
   */
  spawns: Record<string, BimAvatarSpawn>;
}

/** The three states the owner named, in his order. */
export type BimAvatarKind = 'model' | 'icon' | 'none';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function finite(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * A model URL the layer may fetch: a hub path or an http(s) URL, nothing else.
 * `javascript:`/`data:` and any other scheme are refused here rather than at the
 * fetch, so a hand-edited preferences row cannot point the loader somewhere the
 * hub never served.
 */
export function avatarModelUrl(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const url = raw.trim();
  if (!url || url.length > URL_MAX_LENGTH) return null;
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return null;
}

function clampScale(value: unknown): number {
  const scale = finite(value);
  if (scale === null) return 1;
  return Math.min(BIM_AVATAR_MAX_SCALE, Math.max(BIM_AVATAR_MIN_SCALE, scale));
}

/**
 * The whole slot, from whatever the person's row carries — total by contract. A
 * preferences blob is hub data any older build or a hand edit may have written,
 * so a malformed slot reads as "no model, no spawn" instead of throwing inside
 * the people list (a throw in a computed takes the whole view down: the
 * failure mode `references/cloud-payload-field-renames.md` documents).
 */
export function parseAvatarSlot(preferences: unknown): BimAvatarSlot {
  const empty: BimAvatarSlot = { model: null, spawns: {} };
  if (!isRecord(preferences)) return empty;
  const raw = preferences[BIM_AVATAR_PREFS_KEY];
  if (!isRecord(raw)) return empty;

  const modelRaw = isRecord(raw.model) ? raw.model : null;
  const url = modelRaw ? avatarModelUrl(modelRaw.url) : null;
  const model: BimAvatarModel | null =
    modelRaw && url
      ? {
          url,
          name: typeof modelRaw.name === 'string' && modelRaw.name ? modelRaw.name : modelNameFromUrl(url),
          bytes: finite(modelRaw.bytes) ?? 0,
          uploadedAt: typeof modelRaw.uploadedAt === 'string' ? modelRaw.uploadedAt : '',
          scale: clampScale(modelRaw.scale),
        }
      : null;

  return { model, spawns: parseSpawns(raw.spawns) };
}

function parseSpawns(raw: unknown): Record<string, BimAvatarSpawn> {
  const out: Record<string, BimAvatarSpawn> = {};
  if (!isRecord(raw)) return out;
  for (const [dashboardId, value] of Object.entries(raw)) {
    const spawn = parseSpawn(value);
    if (spawn) out[dashboardId] = spawn;
  }
  return out;
}

/** One stored spawn, or null when the point is not a real one. */
export function parseSpawn(raw: unknown): BimAvatarSpawn | null {
  if (!isRecord(raw)) return null;
  const x = finite(raw.x);
  const y = finite(raw.y);
  const z = finite(raw.z);
  if (x === null || y === null || z === null) return null;
  const yawDeg = finite(raw.yawDeg) ?? 0;
  return {
    x,
    y,
    z,
    yawDeg,
    ...(typeof raw.at === 'string' && raw.at ? { at: raw.at } : {}),
  };
}

/** The file name a model URL ends with, for a slot that carries no name. */
export function modelNameFromUrl(url: string): string {
  const leaf = url.split('?')[0].split('/').filter(Boolean).pop() ?? url;
  return decodeURIComponent(leaf);
}

/**
 * The person's kind, in the owner's order: their own model wins, else their hub
 * picture, else nothing at all. `hasImage` is the *resolved* fact (the hub row
 * carries `avatarImageId`), not a guess from the blob.
 */
export function avatarKind(slot: BimAvatarSlot, hasImage: boolean): BimAvatarKind {
  if (slot.model) return 'model';
  return hasImage ? 'icon' : 'none';
}

/**
 * The spawn for one dashboard — the person's HOME spot is that dashboard's own
 * row, never a person-wide fallback (owner, 2026-10-18: *"Home position should be
 * per dashboard settings"*). `null` = this dashboard has no spot for them, and
 * the layer stands them on its preset one.
 */
export function spawnOf(slot: BimAvatarSlot, dashboardId: string): BimAvatarSpawn | null {
  return slot.spawns[dashboardId] ?? null;
}

/**
 * A new slot with one dashboard's spawn written (a pure copy — the caller keeps
 * the rest of the blob, which is what makes the PUT safe: `preferences` is the
 * whole person's settings, and this module only ever touches its own key).
 */
export function withSpawn(slot: BimAvatarSlot, dashboardId: string, spawn: BimAvatarSpawn): BimAvatarSlot {
  if (!dashboardId) return cloneSlot(slot);
  return {
    model: slot.model ? { ...slot.model } : null,
    spawns: { ...slot.spawns, [dashboardId]: { ...spawn } },
  };
}

/** A new slot with the person's model replaced (null clears it back to icon). */
export function withModel(slot: BimAvatarSlot, model: BimAvatarModel | null): BimAvatarSlot {
  return {
    model: model ? { ...model, scale: clampScale(model.scale) } : null,
    spawns: { ...slot.spawns },
  };
}

function cloneSlot(slot: BimAvatarSlot): BimAvatarSlot {
  return {
    model: slot.model ? { ...slot.model } : null,
    spawns: { ...slot.spawns },
  };
}

/**
 * The person's preferences with this module's slot merged in — EVERY other key
 * they hold is carried over untouched, because `PUT /api/users/:id` replaces the
 * whole column. An unreadable blob is replaced by a fresh object (there is
 * nothing readable to keep).
 */
export function withSlotInPreferences(preferences: unknown, slot: BimAvatarSlot): Record<string, unknown> {
  const base = isRecord(preferences) ? { ...preferences } : {};
  base[BIM_AVATAR_PREFS_KEY] = {
    schema: BIM_AVATAR_SCHEMA,
    ...(slot.model ? { model: { ...slot.model } } : {}),
    spawns: { ...slot.spawns },
  };
  return base;
}

/** The model as it is STORED (the writer's side of `parseAvatarSlot`). */
export function toStoredModel(model: BimAvatarModel | null): Record<string, unknown> | null {
  if (!model) return null;
  return {
    url: model.url,
    name: model.name,
    bytes: model.bytes,
    uploadedAt: model.uploadedAt,
    scale: clampScale(model.scale),
  };
}

export interface AvatarBounds {
  min: { x: number; y: number; z: number };
  max: { x: number; y: number; z: number };
}

/**
 * The preset spot for a person with no spawn on this dashboard: just outside the
 * model's own bounds on the -Z face (the side the camera opens on), at the
 * lowest walkable level. It is a *sensible* default, not a claim about the
 * front door — the card's own words are "default: a sensible spot, e.g. site
 * entrance", and the editable part is what matters.
 */
export function defaultSpawn(bounds: AvatarBounds, levelY: number | null, yawDeg = 0): BimAvatarSpawn {
  const y = levelY ?? bounds.min.y;
  return {
    x: (bounds.min.x + bounds.max.x) / 2,
    y,
    z: bounds.min.z - BIM_AVATAR_SPAWN_MARGIN_M,
    yawDeg,
  };
}

/**
 * A spawn pulled into the model's own frame: a point written by an older build
 * (or a hand-edited row) that sits kilometres away is not a place in this
 * house, so the layer keeps the person on the preset spot instead of drawing
 * them in the void. The tolerance is the model's own size plus the margin.
 */
export function clampSpawnToBounds(spawn: BimAvatarSpawn, bounds: AvatarBounds, padding = 50): BimAvatarSpawn {
  const spanX = bounds.max.x - bounds.min.x;
  const spanZ = bounds.max.z - bounds.min.z;
  const looseX = Math.max(spanX * 2, padding);
  const looseZ = Math.max(spanZ * 2, padding);
  const inside =
    spawn.x >= bounds.min.x - looseX &&
    spawn.x <= bounds.max.x + looseX &&
    spawn.z >= bounds.min.z - looseZ &&
    spawn.z <= bounds.max.z + looseZ &&
    spawn.y >= bounds.min.y - padding &&
    spawn.y <= bounds.max.y + padding;
  return inside ? spawn : defaultSpawn(bounds, bounds.min.y, spawn.yawDeg);
}

/** Normalised yaw in degrees, into [0, 360) — and never -0. */
export function normalizeYawDeg(yawDeg: number): number {
  if (!Number.isFinite(yawDeg)) return 0;
  return ((yawDeg % 360) + 360) % 360;
}

/**
 * The yaw a movement direction reads as. The scene's convention is the app's
 * own (`lookFromDirection`): yaw 0 faces -Z and grows counter-clockwise seen
 * from above, so a direction (dx, dz) is `atan2(-dx, -dz)`.
 */
export function yawFromDirection(dx: number, dz: number): number {
  if (!Number.isFinite(dx) || !Number.isFinite(dz) || (dx === 0 && dz === 0)) return 0;
  return normalizeYawDeg((Math.atan2(-dx, -dz) * 180) / Math.PI);
}

/** The shortest way from one yaw to another, in degrees (-180, 180]. */
export function yawDeltaDeg(from: number, to: number): number {
  const delta = normalizeYawDeg(to) - normalizeYawDeg(from);
  if (delta > 180) return delta - 360;
  if (delta <= -180) return delta + 360;
  return delta;
}

/** Ease a facing toward a target by at most `maxStepDeg` (the smooth turn). */
export function approachYawDeg(currentDeg: number, targetDeg: number, maxStepDeg: number): number {
  const delta = yawDeltaDeg(currentDeg, targetDeg);
  if (Math.abs(delta) <= maxStepDeg) return normalizeYawDeg(targetDeg);
  return normalizeYawDeg(currentDeg + Math.sign(delta) * maxStepDeg);
}

/** How fast a driven avatar turns to face where it walks (degrees per second). */
export const BIM_AVATAR_TURN_RATE_DEG = 540;

/**
 * THE FACING CONVENTION (card t_565b83bf) — one rule, shared with the agent
 * FAB's dog, because the owner's report is exactly the mismatch between them:
 * *"i tried walking with our dog glb model.. the model is facing the wrtong
 * way.."*.
 *
 * `useOttoDog.setFacingAngle` writes `model.rotation.y = azimuth` and the FAB
 * feeds it `bimCameraAzimuth` — `Math.atan2(dir.x, dir.z)`, the camera's own
 * look direction. So for a body whose snout must lead along a world direction
 * (dx, dz), the FAB's own answer is:
 *
 *   rotation.y = atan2(dx, dz)
 *
 * and that is what this module hands the layer. The scene's `yawDeg` keeps its
 * own, older meaning — yaw 0 faces −Z (`yawFromDirection`) — so a yaw and a mesh
 * rotation are NOT the same number: the model's own forward is its local +Z, a
 * half turn away from the scene's zero. `avatarFacingRotationY` is the one
 * conversion, and `yawDegFromAvatarRotationY` is its exact inverse, which is
 * what keeps the position gizmo's read-back (`poseOf`) writing the same numbers
 * the drag started with.
 *
 * The identity that proves the two halves agree — asserted in
 * `bimAvatars.test.ts` against the FAB's own formula, not against a copy of it:
 *
 *   avatarFacingRotationY(yawDegFromDirection(dx, dz)) === atan2(dx, dz)
 */
export function avatarFacingRotationY(yawDeg: number): number {
  const yawRad = (normalizeYawDeg(yawDeg) * Math.PI) / 180;
  return yawRad + Math.PI;
}

/** The scene yawDeg a mesh rotation came from — `avatarFacingRotationY`, inverted. */
export function yawDegFromAvatarRotationY(rotationY: number): number {
  if (!Number.isFinite(rotationY)) return 0;
  return normalizeYawDeg(((rotationY - Math.PI) * 180) / Math.PI);
}

/** The scene yawDeg that faces a world direction (dx, dz) — yaw 0 faces −Z. */
export function yawDegFromDirection(dx: number, dz: number): number {
  return yawFromDirection(dx, dz);
}

/**
 * The FAB dog's own azimuth of a world direction, in radians — the value
 * `useOttoDog.setFacingAngle` is handed (`bimCameraAzimuth` is read the same
 * way in `useBimScene.updateCameraAzimuthRefs`). Kept here so a test can state
 * the agreement between the two layers in one line.
 */
export function azimuthRadFromDirection(dx: number, dz: number): number {
  return Math.atan2(dx, dz);
}

/** The clip names the dog (and most character GLBs) use, by intent. */
export const AVATAR_IDLE_CLIP_TERMS = ['idlea', 'idle', 'rest', 'stand', 'breathing', 'default'];
export const AVATAR_WALK_CLIP_TERMS = ['walk_forward_inplace', 'walk_forward', 'walk', 'trot'];

/**
 * The run (owner, 2026-10-13: *"we need running for person"*): played while the
 * walker is BOOSTED (`NAV_BOOST_KEYS` — Shift, or Ctrl), which is the same key the
 * dog's own agent-FAB keymap spells run with.
 */
export const AVATAR_RUN_CLIP_TERMS = ['run_forward_inplace', 'run_forward', 'run', 'jog', 'sprint'];

/** Seated (the dog's own clip name — the agent FAB maps `sit` to `Sitting`). */
export const AVATAR_SIT_CLIP_TERMS = ['sitting', 'sit_up', 'sit_side', 'sit'];

/**
 * Lying down. The dog's rest clips are `Rest_Pose`/`Sitting` rather than a sleep
 * clip, so a character that has neither a `sleep*` nor a `rest*` clip simply has
 * no sleeping position — the menu says so instead of pretending.
 */
export const AVATAR_SLEEP_CLIP_TERMS = ['sleeping', 'sleep', 'rest_pose', 'rest', 'lying', 'lie_down'];

/**
 * What the user asked a person to DO, which decides how the body is placed as
 * well as what it plays (owner, 2026-10-13: *"Sitting, and sleeping animations
 * and positions"*). The clips are authored so the model origin IS the surface the
 * body rests on — standing and sleeping put it on the floor, sitting puts it on
 * the seat — so a position is just the clip plus the point it was asked for.
 */
export type BimAvatarPose = 'stand' | 'sit' | 'sleep';
export const BIM_AVATAR_POSES: readonly BimAvatarPose[] = ['stand', 'sit', 'sleep'];

/** The clip intent one pose plays, or null for the movement-driven default. */
export function poseClipTerms(pose: BimAvatarPose): readonly string[] | null {
  if (pose === 'sit') return AVATAR_SIT_CLIP_TERMS;
  if (pose === 'sleep') return AVATAR_SLEEP_CLIP_TERMS;
  return null;
}

/** Pick a clip out of a model's own animation names, by intent (or null). */
export function pickClipName(names: readonly string[], terms: readonly string[]): string | null {
  const lower = names.map((name) => name.toLowerCase());
  for (const term of terms) {
    const index = lower.findIndex((name) => name.includes(term));
    if (index >= 0) return names[index];
  }
  return null;
}
